import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@finn-auto/design-system/atoms/select';
import { AlertCircle, ArrowLeft, Loader2, User, Users } from 'lucide-react';
import {
  BUFFER_OPTIONS,
  EARLIEST_DAY_OPTIONS,
  EXPIRY_OPTIONS,
  INTERVAL_OPTIONS,
  LENGTH_OPTIONS,
  NOTICE_OPTIONS,
  RANGE_OPTIONS,
  stageOffersFeedbackCall,
  type InterviewLink,
  type InterviewLinkDraft,
  type MeetingType,
} from '../../services/interviewLinksApi';
import { isFirstInterviewStage, isScreeningLink } from '../../services/linkedinShareApi';
import { BookabilityHint } from './BookabilityHint';
import { FeedbackCallEditor } from './FeedbackCallEditor';
import { InterviewerEditor } from './InterviewerEditor';
import { InviteEmailEditor } from './InviteEmailEditor';
import { TimeSuggestionsToggle } from './TimeSuggestionsToggle';
import { LinkedInSettings } from './LinkedInSettings';
import { ExistingRolePicker } from './ExistingRolePicker';
import { FeedbackFormSelect } from './FeedbackFormSelect';
import { PostingPicker } from './PostingPicker';
import { StageSelect } from './StageSelect';
import { TaOwnerPicker } from './TaOwnerPicker';
import { MeetingTypeCard, NumberSelect, TextField } from './linkFormFields';

/**
 * The TA manager's link builder: meeting type, meeting details, interviewers and
 * their availability, link settings, and the confirmation-email toggle. Mirrors
 * the Lever configuration the team already knows.
 */
export function LinkForm({
  draft,
  onChange,
  onSubmit,
  onCancel,
  saving,
  error,
  isEdit,
  existingLinks = [],
}: {
  draft: InterviewLinkDraft;
  onChange: (next: InterviewLinkDraft) => void;
  onSubmit: () => void;
  onCancel: () => void;
  saving: boolean;
  error: string | null;
  isEdit: boolean;
  /** Every link already configured — the source for "add to an existing role". */
  existingLinks?: InterviewLink[];
}) {
  const set = <K extends keyof InterviewLinkDraft>(key: K, value: InterviewLinkDraft[K]) =>
    onChange({ ...draft, [key]: value });

  // The feedback-call default follows the picked stage (offered on case/final,
  // off elsewhere) — but only while CREATING and only until the TA manager
  // touches the section herself. A saved link's config is an explicit choice
  // (the touched flag resets on every mount, so it cannot remember one), and
  // re-picking a stage during an edit must never rewrite it.
  const [feedbackCallTouched, setFeedbackCallTouched] = useState(false);
  const defaultedFeedbackCall = (stageId: string): InterviewLinkDraft['feedbackCall'] => {
    if (isEdit || feedbackCallTouched) return draft.feedbackCall;
    const interviewers = draft.feedbackCall?.interviewers ?? [];
    if (stageOffersFeedbackCall(stageId)) return { enabled: true, interviewers };
    return interviewers.length > 0 ? { enabled: false, interviewers } : null;
  };

  // Mirrors the proxy's validation: a posting id is now mandatory.
  const postingIdValid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    (draft.postingId || '').trim()
  );
  // Mirrors the proxy: an enabled feedback call with nobody to take it is refused.
  const feedbackCallIncomplete =
    draft.feedbackCall?.enabled === true && draft.feedbackCall.interviewers.length === 0;
  // A screening link (Oct 24 2026) has no role, stage, Lever form or feedback call: only the
  // meeting itself is editable, and the proxy keeps the kind and the owner whatever is sent.
  const screening = isScreeningLink(draft);
  const canSave =
    (draft.name || '').trim().length > 0 &&
    draft.interviewers.length > 0 &&
    (screening || (postingIdValid && (draft.stageId || '').trim().length > 0 && !feedbackCallIncomplete));

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSave && !saving) onSubmit();
      }}
    >
      <Button type="button" size="sm" variant="ghost" onClick={onCancel} className="rounded-xl">
        <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
        Back to links
      </Button>

      {screening ? (
        <p className="rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
          Your screening link: a first call candidates book from your LinkedIn InMail. It belongs to
          no role, and a booking writes nothing to Lever — they reach Lever when they reply.
        </p>
      ) : null}

      <section hidden={screening} className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900">
          Meeting Type <span className="text-red-600">*</span>
        </h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <MeetingTypeCard
            active={draft.meetingType === '1:1'}
            icon={User}
            title="1:1 meeting"
            description="The first available interviewer will be scheduled when the candidate selects a time."
            onClick={() => set('meetingType', '1:1' as MeetingType)}
          />
          <MeetingTypeCard
            active={draft.meetingType === 'panel'}
            icon={Users}
            title="Panel meeting"
            description="All interviewers need to be available in order for the candidate to select a time."
            onClick={() => set('meetingType', 'panel' as MeetingType)}
          />
        </div>
      </section>

      <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900">Meeting Details</h2>

        {/*
          First field in the form, full width, because for every stage after a
          role's first one this IS the decision — reuse the role rather than
          hunting the posting down again. It sets the posting and the title
          together, which is what keeps a role's stages under one heading in the
          overview; a title typed slightly differently each time is exactly what
          used to scatter them.
        */}
        <div className="mt-3" hidden={screening}>
          <ExistingRolePicker
            links={existingLinks.filter((l) => !isScreeningLink(l))}
            postingId={draft.postingId || ''}
            onPick={(role) =>
              onChange(
                role
                  ? { ...draft, postingId: role.postingId, jobTitle: role.jobTitle }
                  : { ...draft, postingId: '' }
              )
            }
          />
        </div>

        <label className="mt-3 block">
          <span className="mb-1 block text-xs font-medium text-gray-700">
            Link name <span className="text-red-600">*</span>
          </span>
          <Input
            value={draft.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="Enter link name"
            maxLength={120}
            className="rounded-xl"
          />
          <span className="mt-1 block text-xs text-gray-500">
            Only appears in internal link menus. E.g. “Marketing Phone Screen”
          </span>
        </label>

        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {!screening ? (
            <TextField
              label="Role title (shown to the candidate)"
              value={draft.jobTitle || ''}
              onChange={(v) => set('jobTitle', v)}
              placeholder="Defaults to the link name"
            />
          ) : null}
          {!screening ? (
            <StageSelect
              stageId={draft.stageId || ''}
              onChange={({ stageId, stageName }) =>
                // Leaving First Interview drops "LinkedIn only" — no other stage has a LinkedIn link.
                onChange({
                  ...draft,
                  stageId,
                  stageName,
                  feedbackCall: defaultedFeedbackCall(stageId),
                  audience: draft.audience === 'linkedin' && !isFirstInterviewStage(stageId) ? 'both' : draft.audience,
                })
              }
            />
          ) : null}
          <NumberSelect
            label="Interview length"
            required
            value={draft.minutes}
            options={LENGTH_OPTIONS}
            unit="minutes"
            onChange={(v) => set('minutes', v)}
          />
          <NumberSelect
            label="Interval"
            required
            value={draft.interval}
            options={INTERVAL_OPTIONS}
            unit="minutes"
            onChange={(v) => set('interval', v)}
            hint="How far apart the offered start times are"
          />
          <NumberSelect
            label="Buffer before interview"
            value={draft.bufferBefore}
            options={BUFFER_OPTIONS}
            unit="minutes"
            onChange={(v) => set('bufferBefore', v)}
          />
          <NumberSelect
            label="Buffer after interview"
            value={draft.bufferAfter}
            options={BUFFER_OPTIONS}
            unit="minutes"
            onChange={(v) => set('bufferAfter', v)}
          />
          {!screening ? (
            <>
              <PostingPicker value={draft.postingId || ''} onChange={(v) => set('postingId', v)} />
              {/* After the posting and stage fields: its suggestion is derived from both. */}
              <FeedbackFormSelect
                value={draft.feedbackTemplate || ''}
                postingId={draft.postingId || ''}
                stageId={draft.stageId || ''}
                onChange={(v) => set('feedbackTemplate', v)}
              />
              <TaOwnerPicker value={draft.taOwner || ''} onChange={(v) => set('taOwner', v)} />
            </>
          ) : null}
        </div>
      </section>

      <InterviewerEditor
        meetingType={draft.meetingType}
        interviewers={draft.interviewers}
        onChange={(next) => set('interviewers', next)}
      />

      <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900">Link settings</h2>
        <p className="mt-1 text-xs text-gray-500">Configure how the candidate can use the link.</p>

        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {/*
            No calendar field: every interview is created on the scheduling
            account. It is the identity the proxy authenticates as, so a different
            calendar here could only ever be one it has no right to write to, and
            the organizer is visible to every guest — a personal calendar would put
            someone's name on invitations that should come from the team.
          */}
          {/*
            Each candidate's window, counted from THEIR invite — as in Lever Easy Book. It used to
            read "Link expires after", and the link really did die that many days after it was
            created, which is not what anyone setting it meant. The link now lives until it is
            switched off (or its role closes in Lever).
          */}
          {!screening ? (
            <NumberSelect
              label="Candidate has to book within"
              value={draft.expiryDays}
              options={EXPIRY_OPTIONS}
              unit="days"
              onChange={(v) => set('expiryDays', v)}
              hint="Counted from each candidate's invite. The link itself stays live until you close it"
            />
          ) : null}
          <NumberSelect
            label="Offered time range"
            value={draft.offeredRangeDays}
            options={RANGE_OPTIONS}
            unit="days"
            onChange={(v) => set('offeredRangeDays', v)}
            hint="Days shown to the candidate from when they open the link"
          />
          <NumberSelect
            label="Minimum scheduling notice"
            value={draft.minNoticeHours}
            options={NOTICE_OPTIONS}
            unit="hours"
            onChange={(v) => set('minNoticeHours', v)}
            hint="Candidates cannot pick times sooner than this"
          />
          <NumberSelect
            label="Earliest bookable day"
            value={draft.earliestBookableDays ?? 0}
            options={EARLIEST_DAY_OPTIONS}
            unit="days from today"
            onChange={(v) => set('earliestBookableDays', v)}
            hint="0 = today. Whichever of this and the notice period lands later wins"
          />
        </div>

        {/*
          The two settings above are numbers; this is what they mean today. A
          notice period cannot express "not before Monday" on its own, and neither
          number accounts for weekends or availability windows — which is exactly
          where people's expectations and the booking page part company.
        */}
        <BookabilityHint draft={draft} />
      </section>

      {!screening ? (
        <>
          <LinkedInSettings draft={draft} onChange={onChange} />
          <InviteEmailEditor
            value={draft.inviteEmail ?? null}
            stageId={draft.stageId || ''}
            onChange={(next) => set('inviteEmail', next)}
          />
          <FeedbackCallEditor
            value={draft.feedbackCall ?? null}
            stageId={draft.stageId || ''}
            onChange={(next) => {
              setFeedbackCallTouched(true);
              set('feedbackCall', next);
            }}
          />
        </>
      ) : null}

      <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900">Confirmation Email</h2>
        <label className="mt-3 flex items-start gap-2 text-sm text-gray-800">
          <input
            type="checkbox"
            checked={draft.sendConfirmationEmail}
            onChange={(e) => set('sendConfirmationEmail', e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300"
          />
          <span>
            Send automatic confirmation email to the candidate
            <span className="mt-0.5 block text-xs text-gray-500">
              The candidate is not a calendar attendee, so with this off they receive nothing at all
              — you would have to tell them the time yourself.
            </span>
          </span>
        </label>

        {!screening ? (
          <TimeSuggestionsToggle
            checked={draft.allowTimeSuggestions === true}
            onChange={(v) => set('allowTimeSuggestions', v)}
          />
        ) : null}

        {/*
          The language for the three AUTOMATIC emails only — booked, moved by the candidate,
          cancelled. Nobody is in the loop when those go out, so unlike an invite (where you pick
          DE/EN as you send) and a FINN-side move (where you pick it in the dialog) there is
          nothing else to read a language from. The wording itself lives in the Email Library
          under "Booking confirmations".
        */}
        <label className="mt-4 block max-w-xs">
          <span className="mb-1 block text-xs font-medium text-gray-700">Language</span>
          <Select
            value={draft.language ?? 'en'}
            onValueChange={(v) => set('language', v === 'de' ? 'de' : 'en')}
          >
            <SelectTrigger className="w-full rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="de">German</SelectItem>
            </SelectContent>
          </Select>
          <span className="mt-1 block text-xs text-gray-500">
            Used for the automatic booking, reschedule and cancellation emails. Invitations still
            follow the language you pick when you send them.
          </span>
        </label>
      </section>

      {error ? (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      ) : null}

      <div className="flex items-center gap-2 pb-6">
        <Button type="submit" disabled={!canSave || saving} className="rounded-xl">
          {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
          {isEdit ? 'Save changes' : 'Create link'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} className="rounded-xl">
          Cancel
        </Button>
        {!canSave ? (
          <span className="text-xs text-gray-500">
            {feedbackCallIncomplete
              ? 'The feedback call needs someone to take it — add a person or turn it off.'
              : !postingIdValid && (draft.name || '').trim() && draft.interviewers.length
                ? 'A valid Lever posting id (UUID) is required.'
                : 'A name, a Lever posting, a stage and one interviewer are required.'}
          </span>
        ) : null}
      </div>
    </form>
  );
}
